// roguemixer-app/src/stores/duonamo.js
// Refactored DuoNamo store - simplified logic for loading ALL users

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { supabase } from '../lib/supabase'
import { getGameImage, getAvatarImage } from '../lib/imageUtils'

export const useDuoNamoStore = defineStore('duonamo', () => {
  // Game state
  const gameProgress = ref(null)
  const currentSession = ref(null)
  const currentChallenge = ref(null)
  const allPeople = ref([])
  const sessionStats = ref({
    correct: 0,
    total: 0,
    xpEarned: 0,
    answers: []
  })

  // UI state
  const isGameActive = ref(false)
  const showMotivationalModal = ref(false)
  const currentQuote = ref(null)

  // Computed values
  const currentLevel = computed(() => gameProgress.value?.current_level || 1)
  const totalXP = computed(() => gameProgress.value?.total_xp || 0)
  const accuracy = computed(() => {
    if (sessionStats.value.total === 0) return 0
    return Math.round((sessionStats.value.correct / sessionStats.value.total) * 100)
  })

  // Challenge types with their configurations
  const challengeTypes = {
    guess_name: {
      name: 'Guess the Name',
      difficulty: 1,
      xpReward: 10,
      description: 'Select the correct name for this person'
    },
    guess_face: {
      name: 'Guess the Face',
      difficulty: 1.2,
      xpReward: 12,
      description: 'Select the correct face for this name'
    },
    match_3: {
      name: 'Match Three',
      difficulty: 2,
      xpReward: 25,
      description: 'Connect the correct faces with their names'
    }
  }

  // Initialize game progress for user
  async function initializeGameProgress(userId) {
    try {
      // Check if progress exists
      const { data: existing, error: fetchError } = await supabase
        .from('duonamo_progress')
        .select('*')
        .eq('user_id', userId)
        .single()

      if (fetchError && fetchError.code !== 'PGRST116') {
        throw fetchError
      }

      if (existing) {
        gameProgress.value = existing
      } else {
        // Create new progress
        const { data: newProgress, error: createError } = await supabase
          .from('duonamo_progress')
          .insert({ user_id: userId })
          .select()
          .single()

        if (createError) throw createError
        gameProgress.value = newProgress
      }

      // Load all available people to learn - SIMPLIFIED: ALL USERS
      await loadAllUsers()
    } catch (error) {
      console.error('Error initializing game progress:', error)
      throw error
    }
  }

  // REFACTORED: Load ALL users in the database (not just DuoNamo players)
  async function loadAllUsers() {
    try {
      const { data: people, error } = await supabase
        .from('profiles')
        .select(
          `
          id,
          name,
          thumbnail_url,
          profile_photos(
            id,
            photo_url,
            is_thumbnail
          )
        `
        )
        .not('name', 'is', null)
        .neq('id', gameProgress.value?.user_id) // Exclude current user
        .order('created_at', { ascending: false })

      if (error) throw error

      // Filter out anonymous users and add photo data
      const filtered = people
        .filter(person => person.name && person.name.trim())
        .map(person => ({
          ...person,
          photoUrl: getPersonPhoto(person)
        }))

      // Fetch facts for these users
      const userIds = filtered.map(p => p.id)
      const { data: factsData, error: factsError } = await supabase
        .from('facts')
        .select('user_id, text')
        .in('user_id', userIds)

      if (factsError) throw factsError

      const factsByUser = {}
      factsData?.forEach(f => {
        if (!factsByUser[f.user_id]) factsByUser[f.user_id] = []
        factsByUser[f.user_id].push(f.text)
      })

      allPeople.value = filtered.map(person => ({
        ...person,
        facts: factsByUser[person.id] || []
      }))

      console.log(`Loaded ${allPeople.value.length} people for learning`)
      console.log('Raw people data:', people)
      console.log('Filtered people:', allPeople.value)
      console.log('Current user ID to exclude:', gameProgress.value?.user_id)
    } catch (error) {
      console.error('Error loading all users:', error)
      throw error
    }
  }

  // Get person's photo URL optimized for game use
  function getPersonPhoto(person) {
    let originalUrl

    if (person.thumbnail_url) {
      originalUrl = person.thumbnail_url
    } else {
      const thumbnailPhoto = person.profile_photos?.find(p => p.is_thumbnail)
      if (thumbnailPhoto) {
        originalUrl = thumbnailPhoto.photo_url
      } else {
        originalUrl = person.profile_photos?.[0]?.photo_url || '/placeholder.png'
      }
    }

    return originalUrl ? getGameImage(originalUrl) : '/placeholder.png'
  }

  // SIMPLIFIED: Start a new learning session
  async function startNewSession() {
    try {
      isGameActive.value = true

      // Simplified session logic - determine session type
      const sessionData = await determineSimpleSessionContent()

      // Create session in database
      const { data: session, error } = await supabase
        .from('duonamo_sessions')
        .insert({
          user_id: gameProgress.value.user_id,
          session_type: sessionData.type,
          people_in_session: sessionData.people.map(p => p.id),
          total_challenges: sessionData.challengeCount
        })
        .select()
        .single()

      if (error) throw error

      currentSession.value = session
      sessionStats.value = { correct: 0, total: 0, xpEarned: 0, answers: [] }

      // Generate first challenge
      await generateNextChallenge()
    } catch (error) {
      console.error('Error starting session:', error)
      throw error
    }
  }

  // SIMPLIFIED: Determine session content with simplified logic
  async function determineSimpleSessionContent() {
    // Get current learning progress for all people
    const { data: progress, error } = await supabase
      .from('duonamo_person_progress')
      .select('target_id, mastery_level, confidence_score')
      .eq('learner_id', gameProgress.value.user_id)

    if (error) throw error

    const progressMap = new Map(progress.map(p => [p.target_id, p]))

    // Simplified session logic
    let sessionPeople = []
    let sessionType = ''

    // Get people who need practice (low confidence or never seen)
    const needsPractice = allPeople.value.filter(person => {
      const personProgress = progressMap.get(person.id)
      return !personProgress || personProgress.confidence_score < 0.8
    })

    // Get people who are mastered but need maintenance
    const mastered = allPeople.value.filter(person => {
      const personProgress = progressMap.get(person.id)
      return (
        personProgress &&
        personProgress.mastery_level >= 3 &&
        personProgress.confidence_score >= 0.8
      )
    })

    if (needsPractice.length >= 6) {
      // Learning session - focus on people who need practice
      sessionType = 'learning_new'
      sessionPeople = shuffleArray(needsPractice).slice(0, 8)
    } else if (needsPractice.length >= 3 && mastered.length >= 3) {
      // Mixed session
      sessionType = 'mixed_learning'
      sessionPeople = [
        ...shuffleArray(needsPractice).slice(0, 4),
        ...shuffleArray(mastered).slice(0, 4)
      ]
    } else if (mastered.length >= 6) {
      // Maintenance review
      sessionType = 'maintenance_review'
      sessionPeople = shuffleArray(mastered).slice(0, 8)
    } else {
      // Default: review session with whatever we have
      sessionType = 'review_session'
      sessionPeople = shuffleArray(allPeople.value).slice(0, Math.min(8, allPeople.value.length))
    }

    // Ensure we have people for the session
    if (sessionPeople.length < 3) {
      sessionPeople = shuffleArray(allPeople.value).slice(0, Math.min(6, allPeople.value.length))
      sessionType = 'review_session'
    }

    return {
      type: sessionType,
      people: sessionPeople,
      challengeCount: Math.min(sessionPeople.length * 2, 12) // 2 challenges per person, max 12
    }
  }

  // Generate next challenge
  async function generateNextChallenge() {
    if (sessionStats.value.total >= currentSession.value.total_challenges) {
      await completeSession()
      return
    }

    // Simple challenge type selection
    const progressRatio = sessionStats.value.total / currentSession.value.total_challenges
    let challengeType = ''

    if (progressRatio < 0.5) {
      challengeType = 'guess_name' // Start with easier challenges
    } else if (progressRatio < 0.8) {
      challengeType = Math.random() < 0.7 ? 'guess_name' : 'guess_face'
    } else {
      // Mix in harder challenges toward the end
      const rand = Math.random()
      if (rand < 0.4) challengeType = 'guess_name'
      else if (rand < 0.8) challengeType = 'guess_face'
      else challengeType = 'match_3'
    }

    currentChallenge.value = await createChallenge(challengeType)
  }

  // Create a specific challenge
  async function createChallenge(type) {
    const sessionPeople = currentSession.value.people_in_session
    const availablePeople = allPeople.value.filter(p => sessionPeople.includes(p.id))

    switch (type) {
      case 'guess_name':
        return createGuessNameChallenge(availablePeople)
      case 'guess_face':
        return createGuessFaceChallenge(availablePeople)
      case 'match_3':
        return createMatch3Challenge(availablePeople)
      default:
        throw new Error(`Unknown challenge type: ${type}`)
    }
  }

  // Create guess name challenge
  function createGuessNameChallenge(availablePeople) {
    const targetPerson = availablePeople[Math.floor(Math.random() * availablePeople.length)]
    const distractors = getRandomDistractors(targetPerson, 3)
    const options = shuffleArray([targetPerson, ...distractors])

    const factText =
      targetPerson.facts && targetPerson.facts.length > 0
        ? targetPerson.facts[Math.floor(Math.random() * targetPerson.facts.length)]
        : null

    return {
      type: 'guess_name',
      targetPerson,
      options,
      correctAnswer: targetPerson.id,
      instruction: `Who is this person?`,
      photoUrl: targetPerson.photoUrl,
      fact: factText
    }
  }

  // Create guess face challenge
  function createGuessFaceChallenge(availablePeople) {
    const targetPerson = availablePeople[Math.floor(Math.random() * availablePeople.length)]
    const distractors = getRandomDistractors(targetPerson, 3)
    const options = shuffleArray([targetPerson, ...distractors])

    return {
      type: 'guess_face',
      targetPerson,
      options,
      correctAnswer: targetPerson.id,
      instruction: `Which person is ${targetPerson.name}?`,
      nameToMatch: targetPerson.name
    }
  }

  // Create match 3 challenge
  function createMatch3Challenge(availablePeople) {
    const selectedPeople = shuffleArray(availablePeople).slice(0, 3)
    const names = selectedPeople.map((_, index) => selectedPeople[(index + 1) % selectedPeople.length])

    return {
      type: 'match_3',
      people: selectedPeople,
      // Rotate the names so none starts next to its matching face.
      names,
      instruction: 'Match each face with the correct name',
      matches: selectedPeople.map(person => ({
        id: person.id,
        name: person.name,
        photoUrl: person.photoUrl
      }))
    }
  }

  // Get random people as distractors (wrong answers)
  function getRandomDistractors(targetPerson, count) {
    return shuffleArray(allPeople.value.filter(p => p.id !== targetPerson.id)).slice(0, count)
  }

  // Handle challenge answer - SIMPLIFIED
  async function submitAnswer(userAnswer, responseTime) {
    const isCorrect = checkAnswer(userAnswer)

    // Save challenge record to database
    try {
      // Get the target person ID and correct answer for the database record
      const targetPersonId =
        currentChallenge.value.targetPerson?.id || currentChallenge.value.people?.[0]?.id
      const correctAnswerId =
        currentChallenge.value.correctAnswer || currentChallenge.value.people?.[0]?.id
      const userAnswerId =
        currentChallenge.value.type === 'match_3'
          ? Object.keys(userAnswer)[0] || null // For match_3, take first person ID as representative
          : userAnswer // For guess_name/guess_face, userAnswer is already the ID

      const { error: challengeError } = await supabase.from('duonamo_challenges').insert({
        session_id: currentSession.value.id,
        challenge_type: currentChallenge.value.type,
        target_person_id: targetPersonId,
        options_shown: currentChallenge.value.options?.map(o => o.id) || [],
        user_answer: userAnswerId,
        correct_answer: correctAnswerId,
        is_correct: isCorrect,
        response_time_ms: responseTime,
        completed_at: new Date().toISOString()
      })

      if (challengeError) {
        console.error('Error saving challenge:', challengeError)
      } else {
        console.log('Challenge saved successfully')
      }
    } catch (error) {
      console.error('Error saving challenge record:', error)
    }

    // Update session stats
    sessionStats.value.total++
    sessionStats.value.answers.push(isCorrect)
    if (isCorrect) {
      sessionStats.value.correct++
      const xpGain = calculateXPGain(currentChallenge.value.type, responseTime, true)
      sessionStats.value.xpEarned += xpGain
    }

    // Update person progress using new simplified function
    if (currentChallenge.value.type !== 'match_3') {
      await updatePersonProgress(currentChallenge.value.targetPerson.id, isCorrect, responseTime)
    } else {
      // For match_3, update progress for all people involved
      for (const person of currentChallenge.value.people) {
        const personCorrect = userAnswer[person.id] === person.id
        await updatePersonProgress(person.id, personCorrect, responseTime)
      }
    }

    // Generate next challenge after a brief delay
    setTimeout(async () => {
      await generateNextChallenge()
    }, 1500)

    return {
      isCorrect,
      xpGained: isCorrect ? calculateXPGain(currentChallenge.value.type, responseTime, true) : 0
    }
  }

  // Check if answer is correct
  function checkAnswer(userAnswer) {
    if (currentChallenge.value.type === 'match_3') {
      return currentChallenge.value.people.every(person => userAnswer[person.id] === person.id)
    } else {
      return userAnswer === currentChallenge.value.correctAnswer
    }
  }

  // Calculate XP gain based on performance
  function calculateXPGain(challengeType, responseTime, isCorrect) {
    if (!isCorrect) return 0

    const baseXP = challengeTypes[challengeType].xpReward

    // Time bonus based on response speed
    let timeBonus = 1.0
    if (responseTime < 2000)
      timeBonus = 1.2 // Very fast: 20% bonus
    else if (responseTime < 4000)
      timeBonus = 1.0 // Normal speed: no bonus
    else timeBonus = 0.8 // Slow: 20% penalty

    return Math.round(baseXP * timeBonus)
  }

  // SIMPLIFIED: Update person learning progress using new confidence-based system
  async function updatePersonProgress(personId, isCorrect, responseTime) {
    try {
      const { error } = await supabase.rpc('update_person_progress', {
        p_learner_id: gameProgress.value.user_id,
        p_target_id: personId,
        p_is_correct: isCorrect,
        p_response_time_ms: responseTime
      })

      if (error) throw error
    } catch (error) {
      console.error('Error updating person progress:', error)
    }
  }

  // Complete current session - SIMPLIFIED
  async function completeSession() {
    try {
      // Update session as completed
      const { error: sessionError } = await supabase
        .from('duonamo_sessions')
        .update({
          completed_challenges: sessionStats.value.total,
          correct_answers: sessionStats.value.correct,
          xp_earned: sessionStats.value.xpEarned,
          completed_at: new Date().toISOString(),
          is_completed: true
        })
        .eq('id', currentSession.value.id)

      if (sessionError) throw sessionError

      // Update overall progress
      const newTotalXP = gameProgress.value.total_xp + sessionStats.value.xpEarned
      const newLevel = Math.floor(newTotalXP / 100) + 1 // 100 XP per level

      const { error: progressError } = await supabase
        .from('duonamo_progress')
        .update({
          current_level: newLevel,
          total_xp: newTotalXP,
          last_played_at: new Date().toISOString()
        })
        .eq('user_id', gameProgress.value.user_id)

      if (progressError) throw progressError

      // Update local progress
      gameProgress.value.current_level = newLevel
      gameProgress.value.total_xp = newTotalXP

      // Show motivational quote
      await showMotivationalQuote()

      // Reset game state
      isGameActive.value = false
      currentSession.value = null
      currentChallenge.value = null
    } catch (error) {
      console.error('Error completing session:', error)
      throw error
    }
  }

  // Show motivational quote
  async function showMotivationalQuote() {
    try {
      const { data: quotes, error } = await supabase
        .from('duonamo_quotes')
        .select('*')
        .lte('min_level', gameProgress.value.current_level)
        .order('created_at', { ascending: false })
        .limit(10)

      if (error) throw error

      if (quotes.length > 0) {
        const randomIndex = Math.floor(Math.random() * quotes.length)
        currentQuote.value = quotes[randomIndex]
        showMotivationalModal.value = true
      }
    } catch (error) {
      console.error('Error fetching motivational quote:', error)
    }
  }

  // Get leaderboard data (legacy function)
  async function getLeaderboard(limit = 10) {
    try {
      const { data, error } = await supabase
        .from('duonamo_progress')
        .select(
          `
          user_id,
          current_level,
          total_xp,
          profiles(name, thumbnail_url)
        `
        )
        .order('total_xp', { ascending: false })
        .limit(limit)

      if (error) throw error

      return data.map((entry, index) => {
        const avatarUrl = entry.profiles?.thumbnail_url || '/placeholder.png'
        return {
          rank: index + 1,
          name: entry.profiles?.name || 'Anonymous',
          level: entry.current_level,
          xp: entry.total_xp,
          avatar: getAvatarImage(avatarUrl)
        }
      })
    } catch (error) {
      console.error('Error fetching leaderboard:', error)
      return []
    }
  }

  // Get global leaderboard with comprehensive stats
  async function getGlobalLeaderboard() {
    try {
      // Get all users who have played DuoNamo
      const { data: allProgress, error: progressError } = await supabase
        .from('duonamo_progress')
        .select(
          `
          user_id,
          current_level,
          total_xp
        `
        )
        .order('total_xp', { ascending: false })

      if (progressError) throw progressError

      // Get profile data separately to handle missing profiles
      const userIds = allProgress.map(p => p.user_id)
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('id, name, thumbnail_url')
        .in('id', userIds)

      if (profileError) throw profileError

      // Create a map of profiles for easy lookup
      const profileMap = new Map(profileData.map(p => [p.id, p]))

      // Calculate stats for each user
      const leaderboardData = allProgress.map((progress, index) => {
        // Get profile data for this user
        const profile = profileMap.get(progress.user_id)

        const avatarUrl = profile?.thumbnail_url || '/placeholder.png'

        return {
          rank: index + 1,
          userId: progress.user_id,
          name: profile?.name || 'Anonymous',
          avatar: getAvatarImage(avatarUrl),
          totalXp: progress.total_xp,
          level: progress.current_level,
          isCurrentUser: progress.user_id === gameProgress.value?.user_id
        }
      })

      // Return top 10
      return leaderboardData.slice(0, 10)
    } catch (error) {
      console.error('Error fetching global leaderboard:', error)
      return []
    }
  }

  // Get current user's rank
  async function getCurrentUserRank() {
    if (!gameProgress.value?.user_id) return null

    try {
      // Get all users ordered by XP
      const { data: allProgress, error } = await supabase
        .from('duonamo_progress')
        .select('user_id, total_xp')
        .order('total_xp', { ascending: false })

      if (error) throw error

      // Find current user's position
      const position = allProgress.findIndex(p => p.user_id === gameProgress.value.user_id)
      return position !== -1 ? position + 1 : null
    } catch (error) {
      console.error('Error fetching user rank:', error)
      return null
    }
  }

  // Get current user's stats
  async function getCurrentUserStats() {
    if (!gameProgress.value?.user_id) return null

    try {
      // Get names mastered count
      const { data: masteredNames, error: masteredError } = await supabase
        .from('duonamo_person_progress')
        .select('target_id')
        .eq('learner_id', gameProgress.value.user_id)
        .gte('mastery_level', 3)

      if (masteredError) throw masteredError

      // Get accuracy from sessions
      const { data: userSessions, error: sessionError } = await supabase
        .from('duonamo_sessions')
        .select('correct_answers, completed_challenges')
        .eq('user_id', gameProgress.value.user_id)
        .eq('is_completed', true)

      if (sessionError) throw sessionError

      const totalChallenges = userSessions.reduce(
        (sum, s) => sum + (s.completed_challenges || 0),
        0
      )
      const correctAnswers = userSessions.reduce((sum, s) => sum + (s.correct_answers || 0), 0)
      const accuracy =
        totalChallenges > 0 ? Math.round((correctAnswers / totalChallenges) * 100) : 0

      return {
        namesMastered: masteredNames.length,
        accuracy,
        totalSessions: userSessions.length
      }
    } catch (error) {
      console.error('Error fetching user stats:', error)
      return null
    }
  }

  // SIMPLIFIED: Get user stats with people progress
  async function getUserStats() {
    try {
      console.log('getUserStats called for user:', gameProgress.value?.user_id)

      // Get session data
      const { data: sessions, error: sessionsError } = await supabase
        .from('duonamo_sessions')
        .select('id, xp_earned, correct_answers, total_challenges, completed_at, is_completed')
        .eq('user_id', gameProgress.value.user_id)
        .eq('is_completed', true)

      console.log('Found sessions:', sessions)

      if (sessionsError) throw sessionsError

      // Get challenge data
      const sessionIds = sessions.map(s => s.id)
      let challenges = []
      if (sessionIds.length > 0) {
        const { data: challengeData, error: challengesError } = await supabase
          .from('duonamo_challenges')
          .select('is_correct, response_time_ms, challenge_type')
          .in('session_id', sessionIds)

        if (challengesError) throw challengesError
        challenges = challengeData || []
      }

      // Get people progress
      const { data: peopleProgress, error: progressError } = await supabase
        .from('duonamo_person_progress')
        .select('mastery_level')
        .eq('learner_id', gameProgress.value.user_id)

      if (progressError) throw progressError

      // Calculate stats
      const totalChallenges = challenges.length
      const correctChallenges = challenges.filter(c => c.is_correct).length
      const averageAccuracy =
        totalChallenges > 0 ? Math.round((correctChallenges / totalChallenges) * 100) : 0
      const averageResponseTime =
        challenges.length > 0
          ? Math.round(
              challenges.reduce((sum, c) => sum + c.response_time_ms, 0) / challenges.length
            )
          : 0

      // Challenge type breakdown
      const challengeTypeStats = {}
      for (const type of Object.keys(challengeTypes)) {
        const typeChallenges = challenges.filter(c => c.challenge_type === type)
        challengeTypeStats[type] = {
          total: typeChallenges.length,
          correct: typeChallenges.filter(c => c.is_correct).length,
          accuracy:
            typeChallenges.length > 0
              ? Math.round(
                  (typeChallenges.filter(c => c.is_correct).length / typeChallenges.length) * 100
                )
              : 0
        }
      }

      // People progress breakdown
      const progressCounts = {
        mastered: peopleProgress.filter(p => p.mastery_level >= 3).length,
        practiced: peopleProgress.filter(p => p.mastery_level === 2).length,
        learning: peopleProgress.filter(p => p.mastery_level === 1).length,
        total: allPeople.value.length
      }
      const lastCompletedAt = sessions.reduce((latest, session) => {
        if (!session.completed_at) return latest
        if (!latest) return session.completed_at
        return new Date(session.completed_at) > new Date(latest) ? session.completed_at : latest
      }, null)

      return {
        totalSessions: sessions.length,
        totalChallenges,
        correctChallenges,
        averageAccuracy,
        averageResponseTime,
        challengeTypeStats,
        totalXpEarned: sessions.reduce((sum, s) => sum + s.xp_earned, 0),
        lastCompletedAt,
        currentStreak: await getCurrentStreak(),
        peopleProgress: progressCounts
      }
    } catch (error) {
      console.error('Error fetching user stats:', error)
      return null
    }
  }

  // Get current learning streak
  async function getCurrentStreak() {
    try {
      const { data: sessions, error } = await supabase
        .from('duonamo_sessions')
        .select('completed_at, correct_answers, total_challenges')
        .eq('user_id', gameProgress.value.user_id)
        .eq('is_completed', true)
        .order('completed_at', { ascending: false })
        .limit(20)

      if (error) throw error

      let streak = 0
      for (const session of sessions) {
        const accuracy = (session.correct_answers / session.total_challenges) * 100
        if (accuracy >= 70) {
          streak++
        } else {
          break
        }
      }

      return streak
    } catch (error) {
      console.error('Error calculating streak:', error)
      return 0
    }
  }

  // Utility function to shuffle array
  function shuffleArray(array) {
    const newArray = [...array]
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[newArray[i], newArray[j]] = [newArray[j], newArray[i]]
    }
    return newArray
  }

  return {
    // State
    gameProgress,
    currentSession,
    currentChallenge,
    allPeople,
    sessionStats,
    isGameActive,
    showMotivationalModal,
    currentQuote,

    // Computed
    currentLevel,
    totalXP,
    accuracy,
    challengeTypes,

    // Actions
    initializeGameProgress,
    loadAllUsers,
    startNewSession,
    generateNextChallenge,
    submitAnswer,
    completeSession,
    showMotivationalQuote,
    getLeaderboard,
    getGlobalLeaderboard,
    getCurrentUserRank,
    getCurrentUserStats,
    getUserStats,

    // Utils
    shuffleArray
  }
})
